import type { Clock } from '../ports/Clock';
import type { IdGenerator } from '../ports/IdGenerator';
import type { PhilosophyRepository } from '../ports/PhilosophyRepository';
import { toPhilosophyContentData, type PhilosophyContent } from '../../domain/philosophy/PhilosophyContent';
import type { PhilosophyEntry } from '../../domain/philosophy/PhilosophyEntry';
import type { PhilosophyEntryId } from '../../domain/shared/ids';

interface SeedEntry {
  readonly category: string;
  readonly text: string;
  readonly explanation: string;
  readonly challenge: string;
}

/**
 * All 90 entries are product-authored ("Inspiration stoïcienne"), never presented as historical
 * quotations (capabilities/PHILOSOPHY_ENGINE.md, editorial rules). No classical attribution is
 * claimed anywhere in this corpus, so no provenance or translation-rights verification is owed —
 * the deliberate MVP scope for this mission, distinct from a future editorial pass that could add
 * verified classical quotations alongside this set without changing its structure.
 */
const CORPUS: readonly SeedEntry[] = [
  // action
  {
    category: 'action',
    text: 'Ce qui dépend de toi mérite ton effort ; ce qui n’en dépend pas mérite ta tranquillité.',
    explanation: 'Séparer les deux évite de dépenser de l’énergie sur ce qu’on ne peut pas changer.',
    challenge: 'Nomme une chose de ta journée qui dépend réellement de toi.',
  },
  {
    category: 'action',
    text: 'Agir juste compte plus que réussir vite.',
    explanation: 'La justesse d’une action reste vraie même quand son résultat tarde.',
    challenge: 'Choisis une action aujourd’hui pour sa justesse plutôt que pour sa rapidité.',
  },
  {
    category: 'action',
    text: 'Une action commencée vaut mieux qu’un projet parfait resté immobile.',
    explanation: 'Le commencement, même imparfait, produit déjà de l’information que la planification seule ne donne pas.',
    challenge: 'Démarre la version la plus simple d’une chose que tu repousses.',
  },
  {
    category: 'action',
    text: 'Le contrôle que tu as sur ton attitude reste entier, même quand tout le reste échappe.',
    explanation: 'L’attitude est un des rares terrains sur lesquels le contrôle ne dépend jamais des circonstances.',
    challenge: 'Choisis consciemment ton attitude pour la prochaine heure.',
  },
  {
    category: 'action',
    text: 'Faire ce qui est en ton pouvoir suffit ; le reste ne t’appartient pas.',
    explanation: 'Exiger de soi ce qui dépasse son pouvoir réel produit de la fatigue sans bénéfice.',
    challenge: 'Identifie une exigence que tu portes sur un résultat qui ne dépend pas entièrement de toi.',
  },
  {
    category: 'action',
    text: 'Chaque geste posé aujourd’hui construit la capacité de demain.',
    explanation: 'Une capacité se construit par répétition, rarement par un seul geste isolé.',
    challenge: 'Repère un geste modeste d’aujourd’hui qui sert un objectif plus large.',
  },
  {
    category: 'action',
    text: 'L’intention seule ne change rien ; le geste, même modeste, change quelque chose.',
    explanation: 'Une intention non traduite en geste reste sans effet sur la réalité.',
    challenge: 'Transforme une intention actuelle en un geste concret, même petit.',
  },
  {
    category: 'action',
    text: 'Choisis l’action qui reste juste, indépendamment de son résultat.',
    explanation: 'Le résultat dépend de facteurs multiples ; la justesse de l’action reste, elle, sous ton contrôle.',
    challenge: 'Repense une décision récente uniquement à l’aune de sa justesse, pas de son issue.',
  },
  {
    category: 'action',
    text: 'Ce que tu fais avec constance pèse plus que ce que tu fais avec intensité.',
    explanation: 'Un effort ponctuel, même intense, produit rarement ce que produit une constance modeste.',
    challenge: 'Choisis une action que tu peux répéter demain, même à intensité réduite.',
  },
  // perception
  {
    category: 'perception',
    text: 'Ce n’est pas l’événement qui trouble, mais le jugement qu’on en fait.',
    explanation: 'Le même fait peut produire des réactions très différentes selon la manière dont il est interprété.',
    challenge: 'Reformule un contretemps récent en une phrase strictement factuelle.',
  },
  {
    category: 'perception',
    text: 'Change la manière de regarder une difficulté avant de changer la difficulté elle-même.',
    explanation: 'La lecture d’une situation influence directement les options qu’on est capable d’y voir.',
    challenge: 'Décris une difficulté actuelle sous un angle différent de celui que tu adoptes d’habitude.',
  },
  {
    category: 'perception',
    text: 'Un obstacle observé calmement révèle souvent plus d’options qu’il n’en cache.',
    explanation: 'La panique réduit le champ des solutions perçues ; le calme le rouvre.',
    challenge: 'Face à un obstacle du jour, note trois options avant de choisir.',
  },
  {
    category: 'perception',
    text: 'La même journée peut être vécue comme un poids ou comme un terrain.',
    explanation: 'Les faits d’une journée changent peu ; la manière de s’y situer change beaucoup.',
    challenge: 'Choisis un mot pour décrire ta journée qui ne soit ni dramatique ni minimisant.',
  },
  {
    category: 'perception',
    text: 'Ce que tu redoutes prend souvent plus de place dans l’esprit que dans la réalité.',
    explanation: 'L’anticipation amplifie fréquemment ce que l’expérience réelle relativise ensuite.',
    challenge: 'Écris en une phrase ce que tu redoutes, sans l’amplifier ni le minimiser.',
  },
  {
    category: 'perception',
    text: 'Décrire une situation sans l’exagérer est déjà un acte de lucidité.',
    explanation: 'L’exagération, dans un sens ou dans l’autre, éloigne de ce qui est réellement observable.',
    challenge: 'Reprends une phrase que tu as pensée aujourd’hui et retire-lui tout superlatif.',
  },
  {
    category: 'perception',
    text: 'Le récit qu’on se fait d’un échec compte autant que l’échec lui-même.',
    explanation: 'Un même échec peut devenir une information utile ou une preuve d’incapacité, selon le récit qu’on en fait.',
    challenge: 'Raconte un échec récent comme un fait, sans qualification morale.',
  },
  {
    category: 'perception',
    text: 'Regarder les choses telles qu’elles sont demande plus de courage que de les fuir.',
    explanation: 'Éviter un constat inconfortable coûte souvent plus cher à long terme que de l’affronter directement.',
    challenge: 'Nomme un fait que tu évites de regarder en face en ce moment.',
  },
  {
    category: 'perception',
    text: 'Une contrariété reste proportionnée tant qu’on ne l’amplifie pas soi-même.',
    explanation: 'L’ampleur ressentie d’un désagrément dépend en grande partie de l’attention qu’on lui accorde.',
    challenge: 'Évalue objectivement l’importance réelle d’une contrariété du jour, sur dix.',
  },
  // acceptation
  {
    category: 'acceptation',
    text: 'Accepter un fait ne signifie pas y renoncer, mais cesser de lutter contre ce qui est déjà arrivé.',
    explanation: 'Lutter contre un fait accompli consomme de l’énergie sans pouvoir le modifier.',
    challenge: 'Nomme un fait récent que tu combats encore alors qu’il est déjà arrivé.',
  },
  {
    category: 'acceptation',
    text: 'Ce qui est accompli ne demande plus de combat, seulement une réponse.',
    explanation: 'Une fois un événement passé, la question utile devient « que fait-on maintenant », pas « pourquoi est-ce arrivé ».',
    challenge: 'Choisis une réponse concrète à un fait accompli plutôt qu’un ressassement.',
  },
  {
    category: 'acceptation',
    text: 'Certains jours n’apportent pas ce qu’on attendait ; ils restent des jours légitimes.',
    explanation: 'Un jour qui s’écarte du plan n’est pas un jour raté, seulement un jour différent.',
    challenge: 'Considère ta journée actuelle comme légitime, quelle que soit sa forme.',
  },
  {
    category: 'acceptation',
    text: 'L’imprévu n’est pas une erreur du plan, il en fait partie.',
    explanation: 'Aucun plan ne couvre l’intégralité du réel ; l’imprévu en est une composante normale.',
    challenge: 'Repense un imprévu récent comme une donnée du plan plutôt qu’une anomalie.',
  },
  {
    category: 'acceptation',
    text: 'Composer avec ce qui arrive vaut mieux que résister à ce qui est déjà là.',
    explanation: 'La résistance à un fait déjà présent retarde souvent la réponse qui lui serait utile.',
    challenge: 'Identifie une situation actuelle avec laquelle il vaut mieux composer que lutter.',
  },
  {
    category: 'acceptation',
    text: 'Une contrariété reconnue perd une partie de son poids.',
    explanation: 'Nommer un désagrément, plutôt que le nier, réduit souvent son emprise.',
    challenge: 'Formule à voix basse ou par écrit la contrariété que tu portes aujourd’hui.',
  },
  {
    category: 'acceptation',
    text: 'Ce que tu ne peux pas changer aujourd’hui, tu peux encore le regarder sans y ajouter de colère.',
    explanation: 'La colère ajoutée à une situation immuable en alourdit le poids sans en changer la nature.',
    challenge: 'Observe une situation figée du jour sans y ajouter de jugement.',
  },
  {
    category: 'acceptation',
    text: 'Le calme ne nie pas la difficulté, il en change la charge.',
    explanation: 'Rester calme face à une difficulté ne la minimise pas, cela évite seulement d’y ajouter du poids inutile.',
    challenge: 'Aborde une difficulté du jour avec un ton intérieur volontairement calme.',
  },
  {
    category: 'acceptation',
    text: 'Renoncer à contrôler l’incontrôlable libère l’énergie pour ce qui reste possible.',
    explanation: 'L’énergie dépensée à vouloir contrôler l’incontrôlable n’est plus disponible pour ce qui dépend réellement de soi.',
    challenge: 'Repère une chose que tu essaies de contrôler sans réel pouvoir dessus.',
  },
  // discipline
  {
    category: 'discipline',
    text: 'La discipline construit une liberté future, elle n’est pas une contrainte présente subie.',
    explanation: 'Un cadre choisi aujourd’hui élargit souvent les possibilités disponibles demain.',
    challenge: 'Identifie un cadre que tu tiens aujourd’hui et la liberté qu’il te prépare.',
  },
  {
    category: 'discipline',
    text: 'Un cadre choisi soutient plus qu’un effort de volonté isolé.',
    explanation: 'Un cadre stable réduit le nombre de décisions à reprendre chaque jour, ce que la seule volonté ne fait pas.',
    challenge: 'Fixe un cadre simple pour une habitude que tu maintiens actuellement à la volonté seule.',
  },
  {
    category: 'discipline',
    text: 'La régularité modeste dépasse, à distance, l’intensité ponctuelle.',
    explanation: 'Un effort répété modestement produit, sur la durée, plus qu’un effort intense mais isolé.',
    challenge: 'Choisis une version réduite mais tenable d’une action que tu abandonnes souvent.',
  },
  {
    category: 'discipline',
    text: 'Tenir un engagement simple aujourd’hui prépare la capacité d’en tenir un plus grand demain.',
    explanation: 'La capacité à tenir un engagement se construit progressivement, à partir d’engagements plus modestes.',
    challenge: 'Tiens un engagement volontairement modeste aujourd’hui, jusqu’au bout.',
  },
  {
    category: 'discipline',
    text: 'La constance ne réclame pas la perfection, seulement le retour.',
    explanation: 'Interrompre une constance n’est pas grave en soi ; ne pas y revenir l’est davantage.',
    challenge: 'Reviens aujourd’hui à une habitude que tu as interrompue récemment.',
  },
  {
    category: 'discipline',
    text: 'Ce qui est répété devient une capacité ; ce qui est ponctuel reste un événement.',
    explanation: 'Une action isolée ne construit rien de durable tant qu’elle n’est pas répétée.',
    challenge: 'Répète aujourd’hui une action que tu n’as faite jusqu’ici qu’une seule fois.',
  },
  {
    category: 'discipline',
    text: 'Choisir une limite claire aujourd’hui épargne une négociation avec soi-même demain.',
    explanation: 'Une limite floue laisse la porte ouverte à une renégociation permanente, épuisante à la longue.',
    challenge: 'Fixe une limite claire et écrite pour une décision que tu négocies souvent avec toi-même.',
  },
  {
    category: 'discipline',
    text: 'La rigueur envers soi n’a de valeur que si elle reste respectueuse.',
    explanation: 'Une exigence qui devient dureté cesse de servir la discipline et commence à l’épuiser.',
    challenge: 'Reformule une exigence envers toi-même de façon plus respectueuse.',
  },
  {
    category: 'discipline',
    text: 'Reprendre après une pause fait autant partie de la discipline que la pause elle-même.',
    explanation: 'La discipline ne se mesure pas à l’absence d’interruption, mais à la capacité de reprendre.',
    challenge: 'Planifie précisément le moment où tu reprendras une action interrompue.',
  },
  // relations
  {
    category: 'relations',
    text: 'Une attention sincère offerte ne demande rien en retour pour avoir de la valeur.',
    explanation: 'La valeur d’une attention donnée ne dépend pas de ce qu’elle rapporte en retour.',
    challenge: 'Offre une attention sincère aujourd’hui sans en attendre de réponse particulière.',
  },
  {
    category: 'relations',
    text: 'Écouter pleinement est déjà une forme de présence rare.',
    explanation: 'La plupart des échanges se déroulent avec une attention partagée, jamais totale.',
    challenge: 'Choisis une conversation aujourd’hui pour l’écouter sans préparer ta réponse à l’avance.',
  },
  {
    category: 'relations',
    text: 'Un lien entretenu sans calcul reste plus stable qu’un lien mesuré.',
    explanation: 'Compter précisément ce qu’on donne et reçoit fragilise souvent une relation à long terme.',
    challenge: 'Fais un geste envers un proche sans en évaluer le retour attendu.',
  },
  {
    category: 'relations',
    text: 'La proximité ne se mesure pas au nombre d’échanges, mais à leur qualité.',
    explanation: 'Un échange rare mais sincère peut peser plus qu’un contact fréquent mais superficiel.',
    challenge: 'Consacre un échange du jour entièrement à une seule personne, sans distraction.',
  },
  {
    category: 'relations',
    text: 'Reconnaître la limite d’autrui fait partie du respect qu’on lui porte.',
    explanation: 'Respecter une limite exprimée par quelqu’un d’autre est une forme concrète de considération.',
    challenge: 'Repère une limite qu’une personne proche t’a exprimée et respecte-la explicitement aujourd’hui.',
  },
  {
    category: 'relations',
    text: 'Ce qu’on donne sans en attendre le retour immédiat construit une confiance durable.',
    explanation: 'La confiance se construit dans la durée, rarement par un échange isolé et calculé.',
    challenge: 'Identifie un geste que tu peux offrir sans en attendre de reconnaissance immédiate.',
  },
  {
    category: 'relations',
    text: 'Une relation n’a pas besoin d’être constante pour être réelle.',
    explanation: 'La réalité d’un lien ne se mesure pas seulement à sa fréquence.',
    challenge: 'Renoue avec quelqu’un que tu n’as pas contacté depuis un moment, sans te justifier.',
  },
  {
    category: 'relations',
    text: 'Le silence partagé, quand il est choisi, vaut parfois plus qu’une conversation forcée.',
    explanation: 'Un silence confortable en présence d’une autre personne peut être une forme de proximité réelle.',
    challenge: 'Passe un moment avec quelqu’un aujourd’hui sans chercher à combler chaque silence.',
  },
  {
    category: 'relations',
    text: 'S’intéresser sincèrement à quelqu’un ne coûte rien et change beaucoup.',
    explanation: 'Un intérêt réel porté à une personne demande peu de ressources mais a un effet notable.',
    challenge: 'Pose une question sincère à quelqu’un aujourd’hui et écoute la réponse jusqu’au bout.',
  },
  // recuperation
  {
    category: 'recuperation',
    text: 'Ralentir un jour n’efface pas ce qui a été construit les jours précédents.',
    explanation: 'Une pause ne défait pas les efforts accumulés avant elle.',
    challenge: 'Si tu ralentis aujourd’hui, rappelle-toi une chose déjà construite qui reste acquise.',
  },
  {
    category: 'recuperation',
    text: 'Le repos n’a pas besoin d’être mérité pour être légitime.',
    explanation: 'Le repos reste une nécessité, pas une récompense conditionnée à une performance préalable.',
    challenge: 'Accorde-toi un moment de repos aujourd’hui sans chercher à le justifier.',
  },
  {
    category: 'recuperation',
    text: 'Une journée plus légère a sa place dans une vie tenue avec constance.',
    explanation: 'Une vie régulière comprend naturellement des journées d’intensité variable.',
    challenge: 'Autorise-toi une journée plus légère sans la comparer à une journée idéale.',
  },
  {
    category: 'recuperation',
    text: 'Reconnaître la fatigue n’est pas un aveu de faiblesse, c’est une information exacte.',
    explanation: 'La fatigue décrit un état réel du corps, pas un jugement sur la valeur d’une personne.',
    challenge: 'Nomme ton niveau de fatigue actuel sans le minimiser ni l’exagérer.',
  },
  {
    category: 'recuperation',
    text: 'Reprendre après un ralentissement compte plus que ne jamais ralentir.',
    explanation: 'La capacité à reprendre pèse davantage, sur la durée, que l’absence de toute pause.',
    challenge: 'Si tu as ralenti récemment, choisis maintenant un point de reprise simple.',
  },
  {
    category: 'recuperation',
    text: 'Le corps qui demande une pause exprime une réalité, pas une excuse.',
    explanation: 'Les signaux du corps méritent d’être écoutés au même titre qu’une donnée objective.',
    challenge: 'Écoute un signal du corps aujourd’hui plutôt que de le contourner.',
  },
  {
    category: 'recuperation',
    text: 'Se ménager aujourd’hui prépare la capacité d’avancer demain.',
    explanation: 'Ménager ses ressources n’est pas un renoncement, c’est une gestion réaliste de sa capacité future.',
    challenge: 'Réduis volontairement l’intensité d’une seule chose aujourd’hui.',
  },
  {
    category: 'recuperation',
    text: 'Une pause choisie reste différente d’un abandon.',
    explanation: 'S’arrêter par décision consciente n’a pas le même sens que renoncer par découragement.',
    challenge: 'Distingue clairement, pour toi-même, une pause choisie d’un abandon.',
  },
  {
    category: 'recuperation',
    text: 'Respecter ses limites du jour n’est pas renoncer à ses objectifs.',
    explanation: 'Adapter l’intensité d’une journée à ses limites réelles reste compatible avec un objectif à long terme.',
    challenge: 'Ajuste une seule tâche du jour à ta capacité réelle plutôt qu’à ton ambition initiale.',
  },
  // travail
  {
    category: 'travail',
    text: 'Une heure sans interruption vaut souvent plus qu’une journée fragmentée.',
    explanation: 'La continuité de l’attention change souvent plus le résultat que le nombre d’heures passées.',
    challenge: 'Protège une seule heure aujourd’hui de toute interruption.',
  },
  {
    category: 'travail',
    text: 'Le travail qui compte est rarement le plus visible.',
    explanation: 'Le travail le plus significatif se déroule souvent loin de toute démonstration.',
    challenge: 'Consacre du temps aujourd’hui à une tâche utile mais peu visible.',
  },
  {
    category: 'travail',
    text: 'Terminer une tâche modeste construit plus de confiance qu’en commencer dix inachevées.',
    explanation: 'L’achèvement, même modeste, produit un sentiment de compétence que l’accumulation de débuts ne produit pas.',
    challenge: 'Termine aujourd’hui une tâche modeste laissée en suspens.',
  },
  {
    category: 'travail',
    text: 'La qualité de l’attention portée à une tâche importe autant que sa durée.',
    explanation: 'Un temps court mais pleinement attentif peut produire davantage qu’un temps long mais distrait.',
    challenge: 'Choisis une tâche du jour et donne-lui une attention pleine, même brève.',
  },
  {
    category: 'travail',
    text: 'Un impact réel demande rarement de l’agitation visible.',
    explanation: 'L’agitation apparente n’est pas toujours corrélée à un effet réel sur le résultat.',
    challenge: 'Repère aujourd’hui une action calme qui a pourtant un effet réel.',
  },
  {
    category: 'travail',
    text: 'Choisir une priorité claire simplifie une journée entière.',
    explanation: 'Une priorité explicite réduit le nombre de décisions à prendre au fil de la journée.',
    challenge: 'Nomme une seule priorité pour le reste de ta journée.',
  },
  {
    category: 'travail',
    text: 'Le travail profond commence souvent par éliminer ce qui n’est pas nécessaire.',
    explanation: 'Retirer l’accessoire libère souvent plus de capacité que d’ajouter un effort supplémentaire.',
    challenge: 'Retire un élément superflu de ton espace ou de ta liste avant de commencer.',
  },
  {
    category: 'travail',
    text: 'Une tâche difficile abordée directement pèse moins qu’une tâche évitée longtemps.',
    explanation: 'L’évitement prolongé d’une tâche difficile augmente souvent la charge mentale qu’elle représente.',
    challenge: 'Commence directement la tâche que tu repousses le plus depuis quelques jours.',
  },
  {
    category: 'travail',
    text: 'Avancer d’un pas mesurable vaut mieux que planifier sans agir.',
    explanation: 'Un pas concret, même petit, produit une information que la seule planification ne donne pas.',
    challenge: 'Choisis un pas mesurable et fais-le avant de planifier davantage.',
  },
  // crainte
  {
    category: 'crainte',
    text: 'Nommer une crainte réduit souvent son emprise.',
    explanation: 'Une peur formulée précisément devient plus facile à examiner qu’une peur restée diffuse.',
    challenge: 'Écris en une phrase la crainte qui t’occupe le plus aujourd’hui.',
  },
  {
    category: 'crainte',
    text: 'L’appréhension avant d’agir ne prédit pas l’échec de l’action.',
    explanation: 'Ressentir de l’appréhension est courant avant une action, cela ne présage pas de son issue.',
    challenge: 'Agis malgré une appréhension présente, sans attendre qu’elle disparaisse.',
  },
  {
    category: 'crainte',
    text: 'Ce qu’on évite par crainte garde généralement plus de pouvoir que ce qu’on affronte.',
    explanation: 'L’évitement entretient souvent une peur davantage que la confrontation directe ne le fait.',
    challenge: 'Affronte, même partiellement, une chose que tu évites par crainte.',
  },
  {
    category: 'crainte',
    text: 'La peur d’un jugement extérieur ne devrait pas dicter une décision intérieure.',
    explanation: 'Une décision fondée uniquement sur l’opinion supposée d’autrui perd souvent sa cohérence propre.',
    challenge: 'Prends une décision aujourd’hui en écartant l’opinion supposée d’un tiers.',
  },
  {
    category: 'crainte',
    text: 'Une inquiétude nommée devient un fait à traiter, pas un poids à porter seul.',
    explanation: 'Partager une inquiétude la transforme souvent en problème concret plutôt qu’en fardeau diffus.',
    challenge: 'Partage une inquiétude actuelle avec une personne de confiance.',
  },
  {
    category: 'crainte',
    text: 'L’incertitude fait partie de toute action réelle, elle ne l’invalide pas.',
    explanation: 'Attendre la certitude complète avant d’agir revient souvent à ne jamais agir.',
    challenge: 'Avance sur une action malgré une incertitude que tu ne peux pas lever aujourd’hui.',
  },
  {
    category: 'crainte',
    text: 'Ce qui inquiète aujourd’hui perd souvent de sa netteté dans quelques jours.',
    explanation: 'L’intensité d’une inquiétude diminue fréquemment avec le recul, même sans intervention extérieure.',
    challenge: 'Note une inquiétude actuelle pour la relire dans quelques jours.',
  },
  {
    category: 'crainte',
    text: 'Agir malgré l’inconfort reste possible sans attendre sa disparition.',
    explanation: 'L’inconfort ne disparaît pas toujours avant l’action ; il coexiste souvent avec elle.',
    challenge: 'Fais un pas aujourd’hui en acceptant l’inconfort qui l’accompagne.',
  },
  {
    category: 'crainte',
    text: 'La prudence a de la valeur ; l’évitement systématique en a rarement.',
    explanation: 'La prudence évalue un risque réel ; l’évitement systématique le fuit sans l’évaluer.',
    challenge: 'Distingue, pour une décision actuelle, ce qui relève de la prudence et ce qui relève de l’évitement.',
  },
  // temps
  {
    category: 'temps',
    text: 'Le temps qui passe ne se rattrape pas, mais celui qui reste s’utilise encore.',
    explanation: 'Regretter le temps déjà passé ne restitue rien ; seul le temps restant reste disponible.',
    challenge: 'Choisis une action utile pour le temps qui te reste aujourd’hui.',
  },
  {
    category: 'temps',
    text: 'Une journée bien tenue vaut plus qu’une semaine planifiée dans l’abstrait.',
    explanation: 'Une journée réellement vécue produit plus qu’une planification qui ne se concrétise jamais.',
    challenge: 'Concentre-toi uniquement sur la journée présente plutôt que sur la semaine entière.',
  },
  {
    category: 'temps',
    text: 'Le futur se construit par des choix présents, pas par des intentions différées.',
    explanation: 'Une intention repoussée indéfiniment ne produit aucun effet réel sur l’avenir.',
    challenge: 'Transforme une intention différée en un choix concret dès aujourd’hui.',
  },
  {
    category: 'temps',
    text: 'Ce qui est remis à plus tard n’est pas perdu, seulement déplacé.',
    explanation: 'Reporter une action ne l’annule pas nécessairement, mais en déplace la charge dans le temps.',
    challenge: 'Fixe une date précise pour une chose que tu remets depuis un moment.',
  },
  {
    category: 'temps',
    text: 'Le moment présent reste le seul sur lequel une action a réellement prise.',
    explanation: 'Ni le passé ni le futur ne peuvent recevoir directement une action ; seul le présent le peut.',
    challenge: 'Ramène ton attention sur une seule chose que tu peux faire maintenant.',
  },
  {
    category: 'temps',
    text: 'Une contrainte de temps clarifie souvent une priorité floue.',
    explanation: 'Un délai limité force souvent à distinguer l’essentiel de l’accessoire.',
    challenge: 'Donne une limite de temps courte à une tâche que tu repousses par flou.',
  },
  {
    category: 'temps',
    text: 'Le temps consacré à une chose est toujours retiré à une autre ; choisir reste nécessaire.',
    explanation: 'Le temps est une ressource limitée dont l’usage implique toujours un renoncement ailleurs.',
    challenge: 'Nomme ce à quoi tu renonces en consacrant du temps à une chose précise aujourd’hui.',
  },
  {
    category: 'temps',
    text: 'Ni l’urgence fabriquée ni la lenteur excessive ne servent une journée réelle.',
    explanation: 'Un rythme juste se situe entre la précipitation inutile et l’immobilisme prolongé.',
    challenge: 'Repère un rythme juste pour une tâche du jour, ni précipité ni traîné.',
  },
  {
    category: 'temps',
    text: 'Ce qui prend du temps aujourd’hui économise souvent du temps demain.',
    explanation: 'Un effort de préparation ou de rangement présent réduit fréquemment une charge future.',
    challenge: 'Consacre un peu de temps aujourd’hui à une chose qui t’en fera gagner demain.',
  },
  // clarte
  {
    category: 'clarte',
    text: 'Une décision comprise vaut mieux qu’une décision seulement exécutée.',
    explanation: 'Comprendre pourquoi on agit rend l’action plus facile à tenir dans la durée.',
    challenge: 'Explique-toi en une phrase la raison d’une décision que tu appliques déjà.',
  },
  {
    category: 'clarte',
    text: 'Nommer précisément une difficulté est le premier pas pour la traiter.',
    explanation: 'Une difficulté vague reste difficile à résoudre tant qu’elle n’est pas formulée précisément.',
    challenge: 'Reformule une difficulté actuelle en une phrase précise et concrète.',
  },
  {
    category: 'clarte',
    text: 'La clarté sur ce qu’on veut simplifie la plupart des choix qui suivent.',
    explanation: 'Un objectif clair réduit le nombre d’options à réellement considérer.',
    challenge: 'Formule en une phrase ce que tu veux réellement aujourd’hui.',
  },
  {
    category: 'clarte',
    text: 'Une valeur affirmée dans les faits pèse plus qu’une valeur seulement déclarée.',
    explanation: 'Une valeur ne devient réelle que lorsqu’elle se traduit en actes observables.',
    challenge: 'Traduis une valeur que tu affirmes en un acte concret aujourd’hui.',
  },
  {
    category: 'clarte',
    text: 'Comprendre pourquoi une action compte la rend plus facile à tenir.',
    explanation: 'Le sens attribué à une action soutient la constance nécessaire pour la répéter.',
    challenge: 'Relie une action répétée à la raison profonde qui la justifie.',
  },
  {
    category: 'clarte',
    text: 'La simplicité d’une explication ne diminue pas la validité d’une décision.',
    explanation: 'Une décision peut être juste sans nécessiter une justification complexe.',
    challenge: 'Explique une décision récente en une seule phrase simple.',
  },
  {
    category: 'clarte',
    text: 'Se connaître suffisamment pour anticiper ses propres résistances est déjà une forme de sagesse.',
    explanation: 'Anticiper ses propres réactions habituelles permet de s’y préparer plutôt que d’en être surpris.',
    challenge: 'Anticipe une résistance que tu rencontres habituellement et prépare une réponse.',
  },
  {
    category: 'clarte',
    text: 'Une intention claire résiste mieux à une journée chargée qu’une intention vague.',
    explanation: 'Une intention précise reste identifiable même quand la journée devient dense.',
    challenge: 'Formule une intention claire pour le reste de ta journée, en une phrase.',
  },
  {
    category: 'clarte',
    text: 'Ce qui reste vrai deux ans plus tard mérite d’être choisi aujourd’hui.',
    explanation: 'Un choix aligné avec une valeur durable garde son sens même après un long délai.',
    challenge: 'Évalue une décision du jour en te demandant si elle restera vraie dans deux ans.',
  },
];

export interface SeedPhilosophyCorpusResult {
  readonly created: boolean;
  readonly count: number;
}

export class SeedPhilosophyCorpus {
  public constructor(
    private readonly philosophy: PhilosophyRepository,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  public async execute(): Promise<SeedPhilosophyCorpusResult> {
    const existing = await this.philosophy.list();
    if (existing.length > 0) return { created: false, count: existing.length };

    const base = this.clock.now().getTime();
    for (const [index, seedEntry] of CORPUS.entries()) {
      const timestamp = new Date(base + index * 60_000).toISOString();
      const content: PhilosophyContent = {
        text: seedEntry.text,
        origin: 'Inspiration stoïcienne',
        type: 'original',
        explanation: seedEntry.explanation,
        challenge: seedEntry.challenge,
      };
      const entry: PhilosophyEntry = {
        id: this.ids.generate<PhilosophyEntryId>(),
        title: seedEntry.text,
        category: seedEntry.category,
        content: toPhilosophyContentData(content),
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      await this.philosophy.save(entry);
    }
    return { created: true, count: CORPUS.length };
  }
}
